// Read-only cutover mode for the legacy site.
//
// When the new HotUtils site takes over, this site stays up so players can look at what they had, but
// nothing may change: no game-data refreshes, no writes (settings, templates, squads, mods, events,
// Discord messages). Two layers enforce that:
//   1. UserData.readOnly reports true, which disables every button that already honoured shared-login
//      read-only mode, and the explicit buttons below (refresh, DM, event run) check READ_ONLY_MODE.
//   2. BaseAPI.postToApi refuses every endpoint that is not on the READ_ENDPOINTS allow-list and strips
//      the "refresh from the game" flags from the reads that carry one, so a button that was missed
//      still cannot write. The refusal is the same {errorMessage} shape the API uses, so the existing
//      error dialogs (and App.tsx's unhandled-rejection handler) show it.
// Set READ_ONLY_MODE to false to put the site back to normal.

export const READ_ONLY_MODE = true;

export const NEW_SITE_URL = "https://hotutils.com";

export const READ_ONLY_MESSAGE = "This site is now read only and cannot refresh or change anything. Use the new HotUtils site at hotutils.com.";

/** Short form for a disabled button's tooltip. */
export const READ_ONLY_TITLE = "Not available: this site is read only. Use the new HotUtils site at hotutils.com";

export interface IReadOnlyError
{
    errorMessage: string;
}

export function readOnlyError(): IReadOnlyError
{
    return { errorMessage: READ_ONLY_MESSAGE };
}

// Every POST endpoint the site may still call. Anything not listed is a write (or a game-connection
// login, which stores credentials and pulls data) and is refused. Keep this in sync with the
// BaseAPI/*API call sites: `rg -o 'postToApi\("[^"]+' src`.
const READ_ENDPOINTS = new Set<string>([
    // session + Discord login, and picking which player to view
    "auth/session/register",
    "auth/discord/login",
    "auth/discord/getplayers",
    "auth/link/login",
    "auth/player/login",
    // account + stored player data
    "account/data/all",
    "account/subscription",
    "account/share/list",
    "profile/player/getdata",
    "profile/guild/get",
    "profile/guild/get/minimal",
    // settings (reads only)
    "setting/player/get",
    "setting/guild/get",
    "setting/system/get",
    // game data
    "gamedata/get",
    "gamedata/units",
    "gamedata/tb",
    "gamedata/abbrev/list",
    "imagecompare/meta/get",
    "toon/get/url",
    // mods
    "mods/set/get",
    "mods/task/get",
    // squads, templates, saved data
    "squads/list",
    "squads/get",
    "squads/game/get",
    "templates/list",
    "templates/get",
    "templates/definition",
    "templates/execute",
    "stats/definition/list",
    "stats/definition/get",
    "data/get/1",
    "data/get/2",
    "data/get/3",
    "datastore/player/download",
    "datastore/discord/download",
    "team/list",
    "player/defense/get",
    // guild + events
    "guild/events/get",
    "guild/events/tickets",
    "guild/events/excused/get",
    "guild/events/violations/get",
    "guild/watch/list",
    "personal/events/get",
    "alliance/get",
    "alliance/invite/lookup",
    // TB / TW / GAC / Conquest
    "tb/get",
    "tb/history/get",
    "tb/history/list",
    "tw/get",
    "tw/list",
    "tw/attacks/get",
    "tw/matchhistory",
    "tw/scoreboard",
    "tw/zone/analyze",
    "gac/get",
    "gac/list",
    "gac/v2/token",
    "conquest/get",
    // bot stats
    "hotbot/stats",
]);

/** True when a POST to this endpoint may go out in read-only mode. */
export function isReadOnlyAllowed(endpoint: string, body: any): boolean
{
    if (!READ_ENDPOINTS.has(endpoint))
        return false;
    // account/subscription doubles as "reset my Grand Ivory link" when this flag is set
    if (endpoint === "account/subscription" && body && body.resetGrandIvoryLink)
        return false;
    return true;
}

/**
 * Several reads carry a flag that asks the server to pull fresh data from the game first
 * (`refresh: true`, or `hoursBeforeStale: 0` on the guild reads). Turn those back into plain reads.
 */
export function stripRefreshFlags(body: any): any
{
    if (!body || typeof body !== "object")
        return body;
    let out = body;
    if (out.refresh === true)
        out = { ...out, refresh: false };
    if (out.hoursBeforeStale === 0)
        out = { ...out, hoursBeforeStale: 24 };
    return out;
}
